import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db, functions } from './App';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBeta, setIsBeta] = useState(false);
  const [isFounder, setIsFounder] = useState(false);
  const [betaToken, setBetaToken] = useState(null);
  
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    shopName: '',
    ownerName: '',
    ownerPhone: '',
    shopAddress: '',
    shopCity: '',
    shopState: '',
    shopZip: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      const checkToken = async () => {
        try {
          const docRef = doc(db, 'beta_tokens', token);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && !docSnap.data().used) {
            const data = docSnap.data();
            setIsBeta(true);
            if (data.type === 'founder') {
              setIsFounder(true);
            }
            setBetaToken({ id: token, ...data });
          }
        } catch (err) {
          console.error('Error checking beta token:', err);
        }
      };
      checkToken();
    }
  }, [location]);

  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 6) {
      return '(' + digits.slice(0, 3) + ') ' + digits.slice(3, 6) + '-' + digits.slice(6, 10);
    } else if (digits.length >= 3) {
      return '(' + digits.slice(0, 3) + ') ' + digits.slice(3);
    }
    return digits;
  };

  const handlePhone = (e) => {
    setForm(prev => ({ ...prev, ownerPhone: formatPhone(e.target.value) }));
  };

  const generateShopId = (shopName) => {
    return shopName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 20) + '-' + Math.random().toString(36).slice(2, 6);
  };

  const validateStep1 = () => {
    if (!form.email || !form.email.includes('@')) {
      setError('Please enter a valid email');
      return false;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError(null);
    return true;
  };

  const validateStep2 = () => {
    if (!form.shopName.trim()) {
      setError('Please enter your shop name');
      return false;
    }
    if (!form.ownerName.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (form.ownerPhone.length < 14) {
      setError('Please enter a valid phone number');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);

    try {
      // Create Firebase Auth account
      const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
      const user = userCredential.user;

      // Generate shop ID
      const shopId = generateShopId(form.shopName);

      // Create shop document (pending payment)
      await setDoc(doc(db, 'shops', shopId), {
        shopName: form.shopName.trim(),
        ownerName: form.ownerName.trim(),
        ownerEmail: form.email.trim().toLowerCase(),
        ownerPhone: form.ownerPhone,
        address: {
          street: form.shopAddress.trim(),
          city: form.shopCity.trim(),
          state: form.shopState,
          zip: form.shopZip.trim(),
          full: form.shopAddress.trim() + ', ' + form.shopCity.trim() + ', ' + form.shopState + ' ' + form.shopZip.trim()
        },
        status: 'pending',
        subscriptionStatus: 'pending',
        createdAt: serverTimestamp()
      });

      // Create user document
      await setDoc(doc(db, 'users', user.uid), {
        email: form.email.trim().toLowerCase(),
        name: form.ownerName.trim(),
        role: 'owner',
        shopId: shopId,
        createdAt: serverTimestamp()
      });

      if (isBeta && betaToken) {
        // BETA FLOW
        const activateBetaShop = httpsCallable(functions, 'activateBetaShop');
        await activateBetaShop({
          shopId: shopId,
          token: betaToken.id
        });
        // Force reload to ensure auth/firestore state is clean
        window.location.href = '/dashboard';
      } else {
        // REGULAR FLOW - Redirect to Onboarding for Payment
        // This ensures they use the robust subscription creation flow in ShopOnboarding.js
        window.location.href = '/onboarding';
      }
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else {
        setError(err.message || 'Failed to create account');
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <span className="text-3xl text-amber-400">*</span>
            <span className="text-2xl font-bold text-white">Authorization Hook</span>
          </div>
          <p className="text-slate-400">Create your account</p>
          {isBeta && (
            <div className={'mt-2 inline-block px-3 py-1 rounded-full text-xs font-bold border ' + (isFounder ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' : 'bg-purple-500/20 text-purple-300 border-purple-500/50')}>
              {isFounder ? 'FOUNDING MEMBER INVITE' : 'BETA INVITE ACTIVE'}
            </div>
          )}
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-2">
            <div className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ' + (step >= 1 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-400')}>1</div>
            <div className={'w-12 h-1 rounded ' + (step >= 2 ? 'bg-amber-500' : 'bg-slate-700')}></div>
            <div className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ' + (step >= 2 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-400')}>2</div>
            <div className={'w-12 h-1 rounded ' + (step >= 3 ? 'bg-amber-500' : 'bg-slate-700')}></div>
            <div className={'w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ' + (step >= 3 ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-400')}>3</div>
          </div>
        </div>

        <div className="bg-slate-800 rounded-2xl p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Account Info */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-6">Account Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="you@yourshop.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Password</label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="At least 6 characters"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Confirm Password</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="Confirm your password"
                  />
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => validateStep1() && setStep(2)}
                  className="w-full py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition"
                >
                  Continue
                </button>
              </div>
            )}

            {/* Step 2: Shop Info */}
            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-6">Shop Information</h2>
                
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Shop Name *</label>
                  <input
                    type="text"
                    name="shopName"
                    value={form.shopName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="ABC Auto Repair"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Your Name *</label>
                  <input
                    type="text"
                    name="ownerName"
                    value={form.ownerName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Phone Number *</label>
                  <input
                    type="tel"
                    name="ownerPhone"
                    value={form.ownerPhone}
                    onChange={handlePhone}
                    required
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Shop Address</label>
                  <input
                    type="text"
                    name="shopAddress"
                    value={form.shopAddress}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    placeholder="123 Main Street"
                  />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">City</label>
                    <input
                      type="text"
                      name="shopCity"
                      value={form.shopCity}
                      onChange={handleChange}
                      className="w-full px-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                      placeholder="City"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">State</label>
                    <select
                      name="shopState"
                      value={form.shopState}
                      onChange={handleChange}
                      className="w-full px-2 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                    >
                      <option value="">--</option>
                      {states.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-1">ZIP</label>
                    <input
                      type="text"
                      name="shopZip"
                      value={form.shopZip}
                      onChange={handleChange}
                      className="w-full px-3 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                      placeholder="12345"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="flex-1 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => validateStep2() && setStep(3)}
                    className="flex-1 py-3 bg-amber-500 text-slate-900 font-semibold rounded-lg hover:bg-amber-400 transition"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Review & Pay */}
            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-2">
                  {isFounder ? 'Confirm Founding Membership' : isBeta ? 'Review & Activate Beta' : 'Review & Subscribe'}
                </h2>
                <p className="text-slate-400 text-sm mb-6">Review your information and complete setup</p>

                {/* Summary */}
                <div className="bg-slate-700 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Shop Name</span>
                    <span className="text-white font-medium">{form.shopName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Owner</span>
                    <span className="text-white">{form.ownerName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Email</span>
                    <span className="text-white">{form.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Phone</span>
                    <span className="text-white">{form.ownerPhone}</span>
                  </div>
                </div>

                {/* Pricing / Beta */}
                {isBeta ? (
                  <div className={'border rounded-lg p-4 ' + (isFounder ? 'bg-amber-500/10 border-amber-500/30' : 'bg-purple-500/10 border-purple-500/30')}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium">{isFounder ? 'Founding Member Access' : 'Beta Tester Access'}</span>
                      <span className="text-2xl font-bold text-white">FREE</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">{isFounder ? 'Lifetime Membership' : '60-Day Trial'}</span>
                      <span className={isFounder ? 'text-amber-400' : 'text-purple-400'}>{isFounder ? 'Forever Free' : 'No Credit Card Required'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium">Authorization Hook Monthly</span>
                      <span className="text-2xl font-bold text-white">$50</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-slate-400">Billed monthly</span>
                      <span className="text-emerald-400">Cancel anytime</span>
                    </div>
                  </div>
                )}

                {!isBeta && (
                  <div className="bg-slate-700/50 rounded-lg p-3 flex items-start space-x-2">
                    <svg className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="text-slate-400 text-sm">
                      You'll be redirected to Square's secure checkout to complete payment.
                    </span>
                  </div>
                )}

                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    disabled={loading}
                    className="flex-1 py-3 bg-slate-700 text-white font-semibold rounded-lg hover:bg-slate-600 transition disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className={'flex-1 py-3 text-white font-semibold rounded-lg transition disabled:opacity-50 flex items-center justify-center ' + (isBeta ? 'bg-purple-600 hover:bg-purple-500' : 'bg-emerald-600 hover:bg-emerald-500')}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        {isBeta ? 'Activating...' : 'Creating Account...'}
                      </>
                    ) : (
                      isFounder ? 'Activate Lifetime Access' : isBeta ? 'Start Free Beta' : 'Continue to Payment'
                    )}
                  </button>
                </div>

                <p className="text-center text-slate-500 text-xs mt-4">
                  By subscribing, you agree to our Terms of Service and Privacy Policy.
                </p>
                <p className="text-center text-slate-600 text-xs mt-2">
                  All uploaded images are retained for 30 days then automatically deleted.
                </p>
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="text-amber-400 hover:text-amber-300 font-medium">
                Log in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
